import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, MessageSquare, Send, Tag, HandHelping, Building2, Loader2, Volume2, VolumeX, Play } from "lucide-react";
import { api, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { timeAgo } from "@/lib/razorpay";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lightbox } from "@/components/Lightbox";
import { useAuth } from "@/context/AuthContext";

// Vertical, mobile-first autoplay video (muted) that plays when scrolled into view.
const FeedVideo = ({ src, onExpand }) => {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) el.play().catch(() => {}); else el.pause(); },
      { threshold: 0.6 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div className="relative mt-4 rounded-2xl overflow-hidden bg-black max-w-[400px] mx-auto" data-testid="feed-video">
      <video
        ref={ref}
        src={src}
        muted={muted}
        loop
        playsInline
        preload="metadata"
        onClick={onExpand}
        className="w-full max-h-[560px] object-contain bg-black cursor-pointer"
      />
      <button
        type="button"
        data-testid="feed-video-mute"
        onClick={(e) => { e.stopPropagation(); if (ref.current) { ref.current.muted = !muted; setMuted(!muted); } }}
        className="absolute bottom-3 right-3 h-9 w-9 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
      >
        {muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
      </button>
    </div>
  );
};

const PostMedia = ({ media }) => {
  const [lightbox, setLightbox] = useState(null);
  if (!media || media.length === 0) return null;
  const isVideo = media[0]?.media_type === "video";

  if (isVideo) {
    return (
      <>
        <FeedVideo src={mediaUrl(media[0].url)} onExpand={() => setLightbox(0)} />
        {lightbox != null && <Lightbox items={media} index={lightbox} onClose={() => setLightbox(null)} onIndex={setLightbox} />}
      </>
    );
  }

  const n = media.length;
  const grid = n === 1 ? "grid-cols-1" : "grid-cols-2";
  return (
    <>
      <div data-testid="feed-photos" className={`mt-4 grid ${grid} gap-2 rounded-2xl overflow-hidden`}>
        {media.map((m, i) => (
          <button
            key={i}
            type="button"
            data-testid={`feed-photo-${i}`}
            onClick={() => setLightbox(i)}
            className={`relative overflow-hidden bg-slate-100 ${n === 3 && i === 0 ? "row-span-2" : ""} ${n === 1 ? "max-h-[520px]" : "aspect-square"}`}
          >
            <img src={mediaUrl(m.url)} alt="" className="h-full w-full object-cover hover:opacity-95 transition-opacity" />
          </button>
        ))}
      </div>
      {lightbox != null && <Lightbox items={media} index={lightbox} onClose={() => setLightbox(null)} onIndex={setLightbox} />}
    </>
  );
};

export const PostCard = ({ post }) => {
  const { user } = useAuth();
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [count, setCount] = useState(post.comment_count || 0);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const isOffer = post.type === "Offer";
  const logo = mediaUrl(post.logo_url);

  const toggleComments = async () => {
    const next = !showComments;
    setShowComments(next);
    if (next && comments.length === 0) {
      setLoading(true);
      try {
        const { data } = await api.get(`/posts/${post.id}/comments`);
        setComments(data);
        setCount(data.length);
      } catch (e) { console.error("Failed to load comments", e); } finally { setLoading(false); }
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Please log in to comment");
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/comments`, { content: text });
      setComments((c) => [...c, data]);
      setCount((n) => n + 1);
      setText("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not comment");
    } finally {
      setSending(false);
    }
  };

  return (
    <article data-testid={`post-${post.id}`} className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6">
      <div className="flex items-start gap-3">
        <Link to={`/business/${post.business_id}`} data-testid={`post-avatar-link-${post.id}`} className="h-11 w-11 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center shrink-0 transition-transform hover:-translate-y-0.5 hover:border-brand/40">
          {logo ? <img src={logo} alt={post.business_name} className="h-full w-full object-cover" /> : <Building2 size={20} className="text-slate-300" />}
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/business/${post.business_id}`} data-testid={`post-business-link-${post.id}`} className="font-display font-bold text-slate-900 truncate hover:text-brand hover:underline transition-colors">{post.business_name}</Link>
            {post.gst_verified && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#DCFCE7] border border-[#BBF7D0] px-2 py-0.5 text-[10px] font-bold text-[#166534]">
                <ShieldCheck size={11} className="text-[#22C55E]" /> Verified
              </span>
            )}
            <span className="text-xs text-slate-400">· {timeAgo(post.created_at)}</span>
          </div>
          <span className={`mt-1.5 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${isOffer ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
            {isOffer ? <Tag size={12} /> : <HandHelping size={12} />} {post.type}
          </span>
        </div>
      </div>

      <p className="mt-4 text-slate-700 leading-relaxed whitespace-pre-wrap">{post.content}</p>

      <PostMedia media={post.media} />

      <div className="mt-4 pt-3 border-t border-slate-100">
        <button data-testid={`comments-toggle-${post.id}`} onClick={toggleComments} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-brand transition-colors">
          <MessageSquare size={16} /> {count} {count === 1 ? "comment" : "comments"}
        </button>
      </div>

      {showComments && (
        <div className="mt-4 space-y-3">
          {loading ? (
            <div className="flex justify-center py-2"><Loader2 className="animate-spin text-brand" size={18} /></div>
          ) : (
            comments.map((c) => (
              <div key={c.id} className="flex gap-3">
                <div className="h-8 w-8 rounded-full bg-brand-light flex items-center justify-center shrink-0 text-xs font-bold text-brand">
                  {(c.user_name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="bg-slate-50 rounded-lg px-3 py-2 flex-1">
                  <p className="text-xs font-bold text-slate-700">{c.user_name} <span className="font-normal text-slate-400">· {timeAgo(c.created_at)}</span></p>
                  <p className="text-sm text-slate-600 mt-0.5">{c.content}</p>
                </div>
              </div>
            ))
          )}

          {user ? (
            <form onSubmit={submitComment} className="flex gap-2 pt-1">
              <Input data-testid={`comment-input-${post.id}`} value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment…" className="bg-slate-50 h-10" />
              <Button data-testid={`comment-send-${post.id}`} type="submit" disabled={sending} className="h-10 px-3 bg-brand hover:bg-brand-hover shrink-0">
                {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
              </Button>
            </form>
          ) : (
            <p className="text-sm text-slate-400 pt-1">Log in to join the conversation.</p>
          )}
        </div>
      )}
    </article>
  );
};
